import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { refreshToken, getUserRBAC } from '../store/authSlice';
import { useTokenRefresh } from '../hooks/useTokenRefresh';
import { AppLoader } from '@/shared/components';
import type { AppDispatch, RootState } from '@/app/store';

interface AuthProviderProps {
    children: React.ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
    const dispatch = useDispatch<AppDispatch>();
    const { user } = useSelector((state: RootState) => state.auth);

    // True once the startup refresh attempt has settled (fulfilled or rejected)
    // AND any RBAC chain dispatched on success has also settled. Until this flips
    // true, AuthProvider renders <div>Loading...</div> so the router subtree never
    // sees the intermediate "isAuthenticated=true but user.roles=[]" state.
    const [isInitialised, setIsInitialised] = useState(false);

    // Capture the user value at first render. The startup effect must not re-run
    // when user changes later (e.g. after a successful refresh updates state, or
    // after logout clears it). Using a ref to snapshot the initial value at mount
    // means we can omit user from the effect dependency array without eslint risk,
    // and prevents the cleanup callback from cancelling in-flight async work mid-
    // way through the refresh+RBAC chain.
    const initialUserRef = useRef(user);

    useTokenRefresh();

    useEffect(() => {
        // This effect runs once on mount only (dispatch is stable). It must never
        // re-fire when user changes:
        //
        //   1. After a successful refresh, Redux sets state.auth.user. If user were
        //      in the deps array, React would call the cleanup (setting cancelled=true)
        //      before getUserRBAC() completes, so isInitialised would never flip and
        //      the app would stay on the Loading screen.
        //
        //   2. After logout, user returns to null. A re-fire would silently call
        //      refreshToken() while the HttpOnly refresh cookie is still valid,
        //      re-authenticating a user who explicitly logged out.
        let cancelled = false;

        const initializeAuth = async () => {
            if (initialUserRef.current) {
                // Already authenticated at mount (e.g. mid-session remount). Nothing
                // to do — RBAC was hydrated at login time.
                if (!cancelled) setIsInitialised(true);
                return;
            }

            try {
                const action = await dispatch(refreshToken());
                if (refreshToken.fulfilled.match(action)) {
                    // Hydrate RBAC after a successful refresh, mirroring the login
                    // flow. Awaited so isInitialised does not flip until roles are
                    // populated — prevents PermissionGuard from denying during init.
                    // The full refresh+RBAC chain completes before isInitialised
                    // flips, so protected routes never see an intermediate state
                    // where isAuthenticated=true but user.roles is still empty.
                    await dispatch(getUserRBAC());
                }
            } catch {
                // Silent fail — user is left unauthenticated; ProtectedRoute
                // will redirect once isInitialised flips true.
            } finally {
                if (!cancelled) setIsInitialised(true);
            }
        };

        initializeAuth();

        return () => {
            cancelled = true;
        };
    }, [dispatch]);

    if (!isInitialised) {
        return <AppLoader />;
    }

    return <>{children}</>;
};
